import { useFormContext } from "react-hook-form";
import { PriceMaster, ItemType } from "@/types/system-setting/priceMaster";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { AlertCircle } from "lucide-react";
import PlanBasedPricing from "./PlanBasedPricing";
import GroupBasedPricing from "./GroupBasedPricing";
import SpecialPricingComp from "./SpecialPricing";

const PricingConfig = ({ readOnly = false }: { readOnly?: boolean }) => {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
    register,
  } = useFormContext<PriceMaster>();

  const itemType = watch("item_type");

  const getPriceLabel = () => {
    switch (itemType) {
      case ItemType.Membership:
        return "Membership Price";
      case ItemType.Product:
        return "Product Price";
      case ItemType.CreditAddOns:
        return "Credit Price";
      case ItemType.FacilitySlot:
      // case ItemType.Event:
      default:
        return "Regular Price";
    }
  };

  return (
    <div className="space-y-6">
      <Card className="p-6 shadow-sm border-l-4 border-l-blue-500">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-blue-100 rounded-lg">
            <span className="h-5 w-5 text-blue-600 font-bold text-sm flex items-center justify-center">Rs.</span>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Pricing Configuration</h2>
            <p className="text-sm text-gray-600">Set up pricing options for {itemType}</p>
          </div>
        </div>
        <Separator className="mb-6" />

        {/* Base Pricing */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="p-4 bg-gray-50 rounded-lg border">
            <div className="flex items-center gap-2 mb-3">
              <Badge variant="secondary" className="bg-blue-100 text-blue-800">Required</Badge>
              <h3 className="font-medium text-gray-900">{getPriceLabel()}</h3>
            </div>
            <div className="space-y-2">
              <div className="relative">
                <p className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500 font-medium text-sm">Rs.</p>
                  <Input
                    className="pl-10 max-w-xs"
                    type="number"
                    placeholder="0"
                    step={1}
                    onKeyDown={(e) => {
                      if (e.key === ".") {
                        e.preventDefault();
                      }
                    }}
                    {...register("price", {
                      required: "Price is required",
                      min: { 
                        value: 0, 
                        message: "Price must be 0 or greater" 
                      },
                      validate: (value) => 
                        Number.isInteger(Number(value)) || "Only whole numbers are allowed"
                    })}
                    disabled={readOnly}
                  />
              </div>
              {errors.price && (
                <div className="flex items-center gap-1 text-red-600 text-sm">
                  <AlertCircle className="h-4 w-4" />
                  {errors.price.message}
                </div>
              )}
            </div>
          </div>

          {itemType === ItemType.Membership && (
            <div className="p-4 bg-gray-50 rounded-lg border">
              <div className="flex items-center gap-2 mb-3">
                <Badge variant="outline" className="text-gray-500">Optional</Badge>
                <h3 className="font-medium text-gray-900">Registration Fee</h3>
              </div>
              <div className="space-y-2">
                <div className="relative">
                  <p className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500 font-medium text-sm">Rs.</p>
                  <Input
                    className="pl-10 max-w-xs"
                    type="number"
                    placeholder="0"
                    step={1}
                    onKeyDown={(e) => {
                      if (e.key === ".") {
                        e.preventDefault();
                      }
                    }}
                    {...register("reg_fee", {
                      validate: (value) => 
                        !value || Number.isInteger(Number(value)) || "Only whole numbers are allowed"
                    })}
                    disabled={readOnly}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        
        <PlanBasedPricing control={control} errors={errors} watch={watch} setValue={setValue} readOnly={readOnly} />
        <GroupBasedPricing control={control} errors={errors} watch={watch} setValue={setValue} readOnly={readOnly} />
        <SpecialPricingComp control={control} errors={errors} watch={watch} setValue={setValue} readOnly={readOnly} />
      </Card>
    </div>
  );
};

export default PricingConfig;